import { useState, useEffect, useRef } from "react";

export function useContentRendered(
  docRootRef: React.RefObject<HTMLDivElement | null>,
  safeHtml: string
) {
  const [isContentRendered, setIsContentRendered] = useState(false);
  const mutationObserverRef = useRef<MutationObserver | null>(null);

  useEffect(() => {
    if (!docRootRef.current || !safeHtml) {
      setIsContentRendered(false);
      return;
    }

    // Use MutationObserver to detect when content is actually in the DOM
    const observer = new MutationObserver(() => {
      // Check if doc-root has children (content is rendered)
      if (docRootRef.current && docRootRef.current.children.length > 0) {
        setIsContentRendered(true);
        observer.disconnect();
      }
    });

    // Start observing
    observer.observe(docRootRef.current, {
      childList: true,
      subtree: true,
    });

    // Also check immediately in case content is already rendered
    if (docRootRef.current.children.length > 0) {
      setIsContentRendered(true);
      observer.disconnect();
    }

    mutationObserverRef.current = observer;

    return () => {
      observer.disconnect();
      mutationObserverRef.current = null;
    };
  }, [safeHtml, docRootRef]);

  return isContentRendered;
}
